import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Header } from './components/Header';
import { FilterBar } from './components/FilterBar';
import { CalendarView } from './components/CalendarView';
import { KanbanView } from './components/KanbanView';
import { TableView } from './components/TableView';
import { TimelineView } from './components/TimelineView';
import { AnalyticsView } from './components/AnalyticsView';
import { PostDetailModal } from './components/PostDetailModal';
import { UrlLoadModal } from './components/UrlLoadModal';
import { ToastContainer, CustomDialog, ToastMessage } from './components/ToastDialog';
import { CampaignPost, ViewMode, FilterState, GroupByOption } from './types/calendar';
import { parseCSVContent, exportToCSV } from './utils/csvParser';
import { normalizeCsvUrl } from './utils/urlHelper';
import { storeFileHandle, getStoredFileHandle, clearStoredFileHandle } from './utils/idbHelper';
import { FileSpreadsheet, Upload, Sparkles, FolderOpen, RefreshCw, Globe } from 'lucide-react';
import { DEFAULT_CSV } from './data/defaultCsv';

export function App() {
  // Theme state with localStorage persistence
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('kidz365_theme') as 'light' | 'dark') || 'light';
  });

  // Parse initial state from URL search params
  const getInitialURLParams = () => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view') as ViewMode | null;
    const qParam = params.get('q') || '';
    const platformParam = params.get('platform') ? params.get('platform')!.split(',') : [];
    const phaseParam = params.get('phase') ? params.get('phase')!.split(',') : [];
    const formatParam = params.get('format') ? params.get('format')!.split(',') : [];
    const statusParam = params.get('status') ? params.get('status')!.split(',') : [];
    const postParam = params.get('post') || null;
    const urlParam = params.get('url') || params.get('csv') || params.get('csvUrl') || null;

    return {
      view: (['calendar', 'kanban', 'table', 'timeline', 'analytics'].includes(viewParam || '') ? viewParam : 'calendar') as ViewMode,
      filters: {
        searchQuery: qParam,
        selectedPlatforms: platformParam,
        selectedPhases: phaseParam,
        selectedFormats: formatParam,
        selectedPillars: [],
        selectedStatuses: statusParam,
        startDate: '',
        endDate: '',
      },
      targetPostId: postParam,
      csvUrl: urlParam,
    };
  };

  const initialParams = useMemo(() => getInitialURLParams(), []);

  // Posts State (Starts empty by default!)
  const [posts, setPosts] = useState<CampaignPost[]>([]);
  const [currentView, setCurrentView] = useState<ViewMode>(initialParams.view);
  const [selectedPost, setSelectedPost] = useState<CampaignPost | null>(null);

  // Public CSV URL State & Modal
  const [remoteCsvUrl, setRemoteCsvUrl] = useState<string | null>(initialParams.csvUrl);
  const [isUrlModalOpen, setIsUrlModalOpen] = useState<boolean>(false);

  // Native File Handle State (File System Access API)
  const [fileHandle, setFileHandle] = useState<any | null>(null);
  const [linkedFileName, setLinkedFileName] = useState<string | null>(null);
  const [needsPermission, setNeedsPermission] = useState<boolean>(false);

  // Toast Notifications State
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Dialog State
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    type?: 'success' | 'error' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
  });

  // Filters State
  const [filters, setFilters] = useState<FilterState>(initialParams.filters);
  const hasInitializedRef = useRef<boolean>(false);

  const showToast = (type: 'success' | 'error' | 'info', message: string, title?: string) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id, type, message, title }]);
  };

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Sync state to URL search parameters in real time
  const updateURLParams = useCallback((
    view: ViewMode,
    currentFilters: FilterState,
    post: CampaignPost | null,
    csvUrl?: string | null
  ) => {
    const params = new URLSearchParams();

    if (csvUrl) params.set('url', csvUrl);
    if (view !== 'calendar') params.set('view', view);
    if (currentFilters.searchQuery) params.set('q', currentFilters.searchQuery);
    if (currentFilters.selectedPlatforms.length > 0) params.set('platform', currentFilters.selectedPlatforms.join(','));
    if (currentFilters.selectedPhases.length > 0) params.set('phase', currentFilters.selectedPhases.join(','));
    if (currentFilters.selectedFormats.length > 0) params.set('format', currentFilters.selectedFormats.join(','));
    if (currentFilters.selectedStatuses.length > 0) params.set('status', currentFilters.selectedStatuses.join(','));
    if (post) params.set('post', post.postId);

    const searchStr = params.toString();
    const newUrl = `${window.location.pathname}${searchStr ? '?' + searchStr : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, []);

  // Sync state to URL whenever view, filters, selected post, or remote CSV URL changes
  useEffect(() => {
    updateURLParams(currentView, filters, selectedPost, remoteCsvUrl);
  }, [currentView, filters, selectedPost, remoteCsvUrl, updateURLParams]);

  // Load Public CSV from URL (with automatic Google Sheets link transformation)
  const handleLoadFromUrl = useCallback(async (url: string, showToastNotice: boolean = true): Promise<boolean> => {
    try {
      const fetchUrl = normalizeCsvUrl(url);
      const res = await fetch(fetchUrl);
      if (!res.ok) {
        throw new Error(`Server returned HTTP status ${res.status}`);
      }
      const text = await res.text();
      const loaded = parseCSVContent(text);
      if (!loaded || loaded.length === 0) {
        showToast('error', 'No valid campaign posts found in remote CSV file.', 'Empty CSV');
        return false;
      }
      setPosts(loaded);
      setRemoteCsvUrl(url);
      setFileHandle(null);
      setLinkedFileName(null);
      setNeedsPermission(false);
      clearStoredFileHandle();
      if (showToastNotice) {
        showToast('success', `Loaded ${loaded.length} posts from public URL in Read-Only mode.`, 'Public CSV Loaded');
      }
      if (initialParams.targetPostId) {
        const found = loaded.find(p => p.postId.toLowerCase() === initialParams.targetPostId!.toLowerCase());
        if (found) setSelectedPost(found);
      }
      return true;
    } catch (err: any) {
      console.error('Error fetching CSV from URL:', err);
      showToast('error', `Could not fetch CSV from URL. Make sure access is public: ${err.message || 'CORS or Network error'}`, 'URL Load Failed');
      return false;
    }
  }, [initialParams.targetPostId]);

  // Check URL parameter or IndexedDB for stored file handle on app mount
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const initApp = async () => {
      if (initialParams.csvUrl) {
        await handleLoadFromUrl(initialParams.csvUrl);
        return;
      }

      try {
        const storedHandle = await getStoredFileHandle();
        if (storedHandle) {
          const file = await storedHandle.getFile();
          setLinkedFileName(file.name);

          const perm = await storedHandle.queryPermission({ mode: 'readwrite' });
          if (perm === 'granted') {
            const text = await file.text();
            const loaded = parseCSVContent(text);
            setPosts(loaded);
            setFileHandle(storedHandle);
            showToast('success', `Reconnected to "${file.name}" on disk!`, 'File Reconnected');
            if (initialParams.targetPostId && loaded.length > 0) {
              const found = loaded.find(p => p.postId.toLowerCase() === initialParams.targetPostId!.toLowerCase());
              if (found) setSelectedPost(found);
            }
          } else {
            setFileHandle(storedHandle);
            setNeedsPermission(true);
            const text = await file.text();
            const loaded = parseCSVContent(text);
            setPosts(loaded);
          }
        }
      } catch (err) {
        console.warn('Could not restore stored file handle:', err);
      }
    };

    initApp();
  }, [initialParams.csvUrl, initialParams.targetPostId, handleLoadFromUrl]);

  // Save theme to localStorage & update DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('kidz365_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  // Request write permission for stored handle when user clicks Reconnect
  const handleRequestPermission = async () => {
    if (!fileHandle) return;
    try {
      const perm = await fileHandle.requestPermission({ mode: 'readwrite' });
      if (perm === 'granted') {
        const file = await fileHandle.getFile();
        const text = await file.text();
        const parsed = parseCSVContent(text);
        setPosts(parsed);
        setNeedsPermission(false);
        showToast('success', `Live write sync activated for "${file.name}"!`, 'Sync Active');
      } else {
        showToast('info', 'File permission denied. Operating in read-only mode.', 'Read Only');
      }
    } catch (err) {
      console.error('Error requesting permission:', err);
    }
  };

  // Open Local File using Web File System Access API
  const handleOpenLocalFile = async () => {
    if (!window.showOpenFilePicker) {
      showToast('info', 'Your browser does not support direct file access. Please use standard Upload CSV.', 'Browser Limitation');
      return;
    }

    try {
      const [handle] = await window.showOpenFilePicker({
        types: [
          {
            description: 'CSV Files',
            accept: { 'text/csv': ['.csv'] },
          },
        ],
        multiple: false,
      });

      const file = await handle.getFile();
      const text = await file.text();
      const parsed = parseCSVContent(text);

      setPosts(parsed);
      setFileHandle(handle);
      setLinkedFileName(file.name);
      setNeedsPermission(false);
      setRemoteCsvUrl(null);

      await storeFileHandle(handle);

      showToast('success', `Linked "${file.name}". App will remember this file on next visit!`, 'File Connected');
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Error opening local file:', err);
        showToast('error', 'Could not open local file.', 'Open Failed');
      }
    }
  };

  // Save changes directly back to the linked local file on disk
  const handleSaveToLocalFile = async (currentPosts?: CampaignPost[]) => {
    if (remoteCsvUrl) {
      showToast('info', 'Read-Only Mode: Remote URL datasets cannot be saved back to source.', 'Read-Only');
      return;
    }

    if (!fileHandle) return;

    if (needsPermission) {
      await handleRequestPermission();
    }

    try {
      const postsToSave = currentPosts || posts;
      const csvText = exportToCSV(postsToSave);
      const writable = await fileHandle.createWritable();
      await writable.write(csvText);
      await writable.close();
      if (!currentPosts) {
        showToast('success', `Saved updated campaign data directly to "${linkedFileName}".`, 'Saved to Disk');
      }
    } catch (err: any) {
      console.error('Error saving directly to local file:', err);
      showToast('error', 'Failed to write changes to local file handle. Please check permissions.', 'Save Failed');
    }
  };

  // Standard File Upload Handler
  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) {
        try {
          const parsed = parseCSVContent(text);
          setPosts(parsed);
          setFileHandle(null);
          setLinkedFileName(null);
          setNeedsPermission(false);
          setRemoteCsvUrl(null);
          clearStoredFileHandle();
          showToast('success', `Successfully imported ${parsed.length} posts from ${file.name}.`, 'CSV Loaded');
        } catch (err) {
          showToast('error', 'Failed to parse uploaded CSV file. Please check formatting.', 'Upload Error');
        }
      }
    };
    reader.readAsText(file);
  };

  // Load Optional Sample Dataset
  const handleLoadSampleData = () => {
    try {
      const parsed = parseCSVContent(DEFAULT_CSV);
      setPosts(parsed);
      setRemoteCsvUrl(null);
      showToast('info', 'Loaded sample Kidz365 campaign dataset.', 'Sample Loaded');
    } catch (err) {
      console.error('Failed to load sample dataset:', err);
    }
  };

  // Reset / Clear Data
  const handleResetData = () => {
    setPosts([]);
    setFileHandle(null);
    setLinkedFileName(null);
    setNeedsPermission(false);
    setRemoteCsvUrl(null);
    clearStoredFileHandle();
    const emptyFilters: FilterState = {
      searchQuery: '',
      selectedPlatforms: [],
      selectedPhases: [],
      selectedFormats: [],
      selectedPillars: [],
      selectedStatuses: [],
      startDate: '',
      endDate: '',
    };
    setFilters(emptyFilters);
    updateURLParams(currentView, emptyFilters, null, null);
    showToast('info', 'Cleared active campaign dataset.', 'Dataset Cleared');
  };

  // Export filtered posts to CSV download
  const handleExportData = () => {
    const csvString = exportToCSV(filteredPosts);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Kidz365_Campaign_Calendar_Export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('success', `Exported ${filteredPosts.length} posts to CSV download.`, 'Export Ready');
  };

  // Share Event / Post Deep Link
  const handleSharePost = (post: CampaignPost) => {
    const url = new URL(window.location.href);
    url.searchParams.set('post', post.postId);
    navigator.clipboard.writeText(url.toString());
    showToast('success', `Copied shareable link for post ${post.postId}!`, 'Link Copied');
  };

  // Share View URL
  const handleShareView = () => {
    navigator.clipboard.writeText(window.location.href);
    showToast('success', 'Copied shareable URL link for current view and filters to clipboard!', 'View Link Copied');
  };

  // Update individual post status
  const handleUpdateStatus = (postId: string, newStatus: string) => {
    if (remoteCsvUrl) {
      showToast('info', 'Read-Only Mode: Changes cannot be saved back to public URL datasets.', 'Read-Only Mode');
      return;
    }

    const updated = posts.map(p => p.postId === postId ? { ...p, status: newStatus } : p);
    setPosts(updated);

    if (selectedPost && selectedPost.postId === postId) {
      setSelectedPost(prev => prev ? { ...prev, status: newStatus } : null);
    }

    if (fileHandle) {
      handleSaveToLocalFile(updated);
      showToast('success', `Status updated to "${newStatus}" & synced to disk!`, 'Auto Saved');
    } else {
      showToast('info', `Status updated to "${newStatus}".`, 'Post Updated');
    }
  };

  // Update post property via Drag & Drop in Kanban
  const handleUpdatePostField = (postId: string, field: GroupByOption, newValue: string) => {
    if (remoteCsvUrl) {
      showToast('info', 'Read-Only Mode: Drag & drop editing is disabled for public URL datasets.', 'Read-Only Mode');
      return;
    }

    const fieldKeyMap: Record<GroupByOption, keyof CampaignPost> = {
      status: 'status',
      phase: 'phase',
      platform: 'platform',
      format: 'format',
    };
    const targetKey = fieldKeyMap[field];
    const updated = posts.map(p => p.postId === postId ? { ...p, [targetKey]: newValue } : p);
    setPosts(updated);

    if (selectedPost && selectedPost.postId === postId) {
      setSelectedPost(prev => prev ? { ...prev, [targetKey]: newValue } : null);
    }

    if (fileHandle) {
      handleSaveToLocalFile(updated);
      showToast('success', `Moved post ${postId} → ${newValue} & synced to disk!`, 'Drag & Drop Saved');
    } else {
      showToast('info', `Moved post ${postId} → ${newValue}`, 'Post Updated');
    }
  };

  // Extract available filter options dynamically
  const availablePlatforms = useMemo(() => {
    return Array.from(new Set(posts.map(p => p.platform).filter(Boolean))).sort();
  }, [posts]);

  const availablePhases = useMemo(() => {
    return Array.from(new Set(posts.map(p => p.phase).filter(Boolean))).sort();
  }, [posts]);

  const availableFormats = useMemo(() => {
    return Array.from(new Set(posts.map(p => p.format).filter(Boolean))).sort();
  }, [posts]);

  const availablePillars = useMemo(() => {
    return Array.from(new Set(posts.map(p => p.contentPillar).filter(Boolean))).sort();
  }, [posts]);

  const availableStatuses = useMemo(() => {
    return Array.from(new Set(posts.map(p => p.status).filter(Boolean))).sort();
  }, [posts]);

  // Compute Filtered Posts
  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      // Search query filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesQuery =
          post.postId.toLowerCase().includes(query) ||
          post.hook.toLowerCase().includes(query) ||
          post.caption.toLowerCase().includes(query) ||
          post.hashtags.toLowerCase().includes(query) ||
          post.visualDirection.toLowerCase().includes(query) ||
          post.cta.toLowerCase().includes(query) ||
          post.contentPillar.toLowerCase().includes(query);
        if (!matchesQuery) return false;
      }

      // Platform filter
      if (filters.selectedPlatforms.length > 0) {
        if (!filters.selectedPlatforms.includes(post.platform)) return false;
      }

      // Phase filter
      if (filters.selectedPhases.length > 0) {
        if (!filters.selectedPhases.includes(post.phase)) return false;
      }

      // Format filter
      if (filters.selectedFormats.length > 0) {
        if (!filters.selectedFormats.includes(post.format)) return false;
      }

      // Pillar filter
      if (filters.selectedPillars.length > 0) {
        if (!filters.selectedPillars.includes(post.contentPillar)) return false;
      }

      // Status filter
      if (filters.selectedStatuses.length > 0) {
        if (!filters.selectedStatuses.includes(post.status)) return false;
      }

      return true;
    });
  }, [posts, filters]);

  return (
    <div className="app-container">
      {/* Header Bar */}
      <Header
        currentView={currentView}
        onViewChange={setCurrentView}
        totalPosts={posts.length}
        filteredCount={filteredPosts.length}
        theme={theme}
        onToggleTheme={toggleTheme}
        onFileUpload={handleFileUpload}
        onOpenLocalFile={handleOpenLocalFile}
        onSaveToLocalFile={() => handleSaveToLocalFile()}
        linkedFileName={linkedFileName}
        needsPermission={needsPermission}
        onRequestPermission={handleRequestPermission}
        onResetData={handleResetData}
        onExportData={handleExportData}
        remoteCsvUrl={remoteCsvUrl}
        isReadOnly={!!remoteCsvUrl}
        onOpenUrlModal={() => setIsUrlModalOpen(true)}
      />

      {/* Universal Search & Filter Bar */}
      {posts.length > 0 && (
        <FilterBar
          filters={filters}
          onFilterChange={setFilters}
          availablePlatforms={availablePlatforms}
          availablePhases={availablePhases}
          availableFormats={availableFormats}
          availablePillars={availablePillars}
          availableStatuses={availableStatuses}
          onShareView={handleShareView}
        />
      )}

      {/* Main View Renderer */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {posts.length === 0 ? (
          /* Empty State Hero Screen */
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem 1.5rem',
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{
              maxWidth: '560px',
              width: '100%',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '2.5rem 2rem',
              boxShadow: 'var(--shadow-xl)',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.25rem',
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
              }}>
                <Sparkles size={32} />
              </div>

              <div>
                <h2 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  color: 'var(--text-main)',
                  marginBottom: '0.4rem',
                }}>
                  No Campaign Data Loaded
                </h2>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Open a local CSV file, load a public CSV file via URL to view & share with your team, or upload a CSV file.
                </p>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', width: '100%', marginTop: '0.5rem' }}>
                <button
                  onClick={() => setIsUrlModalOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.65rem 1.25rem',
                    borderRadius: '10px',
                    border: '1px solid #3b82f6',
                    background: 'rgba(59, 130, 246, 0.12)',
                    color: '#3b82f6',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Globe size={18} />
                  Load Public CSV URL
                </button>

                <button
                  onClick={handleOpenLocalFile}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.65rem 1.25rem',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'var(--accent-primary)',
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FileSpreadsheet size={18} />
                  Open Local CSV (Live Sync)
                </button>

                <button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.csv';
                    input.onchange = (e: any) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    };
                    input.click();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.65rem 1.25rem',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-main)',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Upload size={18} />
                  Upload CSV File
                </button>
              </div>

              <div style={{ marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)', width: '100%' }}>
                <button
                  onClick={handleLoadSampleData}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-light)',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Or click here to load sample Kidz365 demo dataset
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {currentView === 'calendar' && (
              <CalendarView posts={filteredPosts} onSelectPost={setSelectedPost} />
            )}
            {currentView === 'kanban' && (
              <KanbanView
                posts={filteredPosts}
                onSelectPost={setSelectedPost}
                onUpdatePostField={handleUpdatePostField}
                isReadOnly={!!remoteCsvUrl}
              />
            )}
            {currentView === 'table' && (
              <TableView posts={filteredPosts} onSelectPost={setSelectedPost} />
            )}
            {currentView === 'timeline' && (
              <TimelineView posts={filteredPosts} onSelectPost={setSelectedPost} />
            )}
            {currentView === 'analytics' && (
              <AnalyticsView posts={filteredPosts} onSelectPost={setSelectedPost} />
            )}
          </>
        )}
      </main>

      {/* Post Detail Drawer Modal */}
      <PostDetailModal
        post={selectedPost}
        onClose={() => setSelectedPost(null)}
        onUpdateStatus={handleUpdateStatus}
        onSharePost={handleSharePost}
        isReadOnly={!!remoteCsvUrl}
      />

      {/* Public CSV URL Loader Modal */}
      <UrlLoadModal
        isOpen={isUrlModalOpen}
        onClose={() => setIsUrlModalOpen(false)}
        onLoadUrl={handleLoadFromUrl}
        currentUrl={remoteCsvUrl}
      />

      {/* Custom Toast Container */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Custom Dialog Modal */}
      <CustomDialog
        isOpen={dialogState.isOpen}
        type={dialogState.type}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={() => setDialogState({ isOpen: false, title: '', message: '' })}
      />
    </div>
  );
}
